import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { StatusBadge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/States';
import { cn } from '@/lib/cn';

const WARNA_LEVEL = {
  error: 'text-danger-500',
  warn: 'text-warning-500',
  info: 'text-info-500',
  debug: 'text-[var(--text-muted)]',
};

/**
 * Tampilan log gaya konsol: satu baris per langkah, waktu di kiri, tingkat
 * keparahan berwarna, pesan di kanan.
 *
 * Baris terakhir digulirkan ke bawah sendiri selama eksekusi masih berjalan,
 * tetapi TIDAK setelah selesai - menggulirkan paksa layar yang sedang dibaca
 * orang adalah gangguan, bukan bantuan.
 */
export const LogView = ({ logs = [], berjalan = false, className }) => {
  const akhir = useRef(null);

  useEffect(() => {
    if (berjalan) akhir.current?.scrollIntoView({ block: 'nearest' });
  }, [logs.length, berjalan]);

  if (!logs.length) {
    return (
      <p className="px-5 py-8 text-center text-sm text-[var(--text-muted)]">
        {berjalan ? 'Menunggu catatan pertama...' : 'Belum ada catatan log.'}
      </p>
    );
  }

  return (
    <div
      className={cn(
        'max-h-96 overflow-y-auto rounded-lg bg-[var(--color-void-950)] p-3 font-mono text-xs leading-relaxed',
        className,
      )}
    >
      {logs.map((baris, i) => (
        <div key={i} className="flex gap-3 py-px">
          <span className="shrink-0 text-white/30">
            {new Date(baris.at).toLocaleTimeString('id-ID', { hour12: false })}
          </span>
          <span className={cn('w-10 shrink-0 font-bold uppercase', WARNA_LEVEL[baris.level])}>
            {baris.level}
          </span>
          <span className="min-w-0 break-words whitespace-pre-wrap text-white/75">
            {baris.message}
          </span>
        </div>
      ))}
      <div ref={akhir} />
    </div>
  );
};

/**
 * Log satu eksekusi skrip template, memuat ulang sendiri selama masih
 * berjalan lalu berhenti begitu selesai.
 */
export const ScriptRunLogDialog = ({ runId, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['script-run', runId],
    queryFn: () => api.get(`/script-runs/${runId}`),
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status;
      return status === 'queued' || status === 'running' ? 1500 : false;
    },
  });

  const run = data?.data;
  const berjalan = run?.status === 'queued' || run?.status === 'running';

  return (
    <Modal
      open
      onClose={onClose}
      title={run?.templateName ?? 'Eksekusi Skrip'}
      description={run?.scriptPath}
      size="lg"
    >
      {isLoading || !run ? (
        <PageLoader label="Memuat log..." />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <StatusBadge status={run.status} size="sm" />
            {run.result?.rowCount != null && (
              <span className="text-[var(--text-secondary)]">{run.result.rowCount} baris</span>
            )}
            {run.startedAt && (
              <span className="text-[var(--text-muted)]">
                Mulai {new Date(run.startedAt).toLocaleTimeString('id-ID')}
              </span>
            )}
            {run.finishedAt && (
              <span className="text-[var(--text-muted)]">
                Selesai {new Date(run.finishedAt).toLocaleTimeString('id-ID')}
              </span>
            )}
          </div>

          {run.error && (
            <p className="rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700 dark:border-danger-500/25 dark:bg-danger-500/10 dark:text-danger-500">
              {run.error}
            </p>
          )}

          <LogView logs={run.logs} berjalan={berjalan} />
        </div>
      )}
    </Modal>
  );
};
